import { ORIGINAL_COLLISION_MANIFEST } from "../data/generated/original-collision-manifest";

export type OriginalCollisionDecision = {
  normalizedTile: number;
  tilePassable: boolean;
  eventActivationDecoded: boolean;
  eventPresent: boolean;
  blockedEventRecheckDecoded: boolean;
  evidence: {
    getMpdStatementId: string;
    getMsgMpdStatementId: string;
    passabilityStatementIds: readonly string[];
    eventActivationStatementIds: readonly string[];
    blockedEventCheckStatementIds: readonly string[];
  };
};

type StatementRef = {
  statementId: string;
  statementText?: string;
};

type AssignmentRef = StatementRef & {
  expressionText: string;
};

type CallRef = StatementRef & {
  id: string;
  name: string;
  argumentTexts: readonly string[];
};

type TileLookupRule = {
  name: string;
  statements: readonly StatementRef[];
  assignments: readonly AssignmentRef[];
};

type CollisionRule = {
  statements: readonly StatementRef[];
  calls?: readonly CallRef[];
};

const DECODED_COLLISION = ORIGINAL_COLLISION_MANIFEST.decodedCollisionRules;
const TILE_LOOKUP_RULES = DECODED_COLLISION.tileLookupRules as unknown as readonly TileLookupRule[];
const GET_MPD_RULE = requireTileLookupRule("GetMPD");
const GET_MSG_MPD_RULE = requireTileLookupRule("GetMSGMPD");
const PASSABILITY_RULES = DECODED_COLLISION.tilePassabilityRules as unknown as readonly CollisionRule[];
const EVENT_ACTIVATION_RULES = DECODED_COLLISION.eventActivationRules as unknown as readonly CollisionRule[];
const BLOCKED_EVENT_CHECK_RULES = DECODED_COLLISION.blockedEventCheckRules as unknown as readonly CollisionRule[];

export const ORIGINAL_COLLISION_RUNTIME_CONSUMPTION = {
  manifest: "docs/original-collision-manifest.json",
  generatedManifestModule: "web/src/data/generated/original-collision-manifest.ts",
  consumesDecodedCollision: true,
  sourceBackedStatus: ORIGINAL_COLLISION_MANIFEST.status,
  sourceBackedCriteria: {
    sourceIrCollisionProceduresDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.sourceIrCollisionProceduresDecoded,
    sourceBackedCollisionStatementIds: ORIGINAL_COLLISION_MANIFEST.criteria.sourceBackedCollisionStatementIds,
    getMpdTextSourceIrDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.getMpdTextSourceIrDecoded,
    getMsgMpdTextSourceIrDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.getMsgMpdTextSourceIrDecoded,
    tilePassabilityDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.tilePassabilityDecoded,
    eventActivationDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.eventActivationDecoded,
    blockedEventCheckDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.blockedEventCheckDecoded,
    mapTransitionRulesDecoded: ORIGINAL_COLLISION_MANIFEST.criteria.mapTransitionRulesDecoded,
    mapTransitionRulesScopedToEventCommandGate:
      ORIGINAL_COLLISION_MANIFEST.criteria.mapTransitionRulesScopedToEventCommandGate,
    frmFullDecodeNotRequiredForCollisionGate:
      ORIGINAL_COLLISION_MANIFEST.criteria.frmFullDecodeNotRequiredForCollisionGate
  },
  consumedStatementIds: {
    getMpd: firstAssignment("GetMPD assignment", GET_MPD_RULE.assignments).statementId,
    getMsgMpd: firstAssignment("GetMSGMPD assignment", GET_MSG_MPD_RULE.assignments).statementId,
    passability: PASSABILITY_RULES.flatMap((rule) => statementIds(rule.statements)),
    eventActivation: EVENT_ACTIVATION_RULES.flatMap((rule) => statementIds(rule.statements)),
    blockedEventCheck: BLOCKED_EVENT_CHECK_RULES.flatMap((rule) => statementIds(rule.statements))
  }
} as const;

export function originalCollisionRuntimeConsumesDecodedCollision(): boolean {
  return ORIGINAL_COLLISION_RUNTIME_CONSUMPTION.consumesDecodedCollision;
}

export function originalGetMpdFromRawTile(rawTile: number): number {
  return rawTile - 128;
}

export function originalGetMpdFromNormalizedTile(normalizedTile: number): number {
  return normalizedTile;
}

export function originalGetMsgMpdFromEventIndex(eventIndex: number | null): number {
  return eventIndex == null || eventIndex < 0 ? -1 : eventIndex;
}

export function originalTilePassability(normalizedTile: number): boolean {
  return originalGetMpdFromNormalizedTile(normalizedTile) <= 31;
}

export function originalCollisionDecision(options: {
  normalizedTile: number;
  eventIndex: number | null;
}): OriginalCollisionDecision {
  const eventValue = originalGetMsgMpdFromEventIndex(options.eventIndex);
  return {
    normalizedTile: originalGetMpdFromNormalizedTile(options.normalizedTile),
    tilePassable: originalTilePassability(options.normalizedTile),
    eventActivationDecoded: eventValue >= 0,
    eventPresent: eventValue >= 0,
    blockedEventRecheckDecoded: true,
    evidence: {
      getMpdStatementId: firstAssignment("GetMPD assignment", GET_MPD_RULE.assignments).statementId,
      getMsgMpdStatementId: firstAssignment("GetMSGMPD assignment", GET_MSG_MPD_RULE.assignments).statementId,
      passabilityStatementIds: PASSABILITY_RULES.flatMap((rule) => statementIds(rule.statements)),
      eventActivationStatementIds: EVENT_ACTIVATION_RULES.flatMap((rule) => statementIds(rule.statements)),
      blockedEventCheckStatementIds: BLOCKED_EVENT_CHECK_RULES.flatMap((rule) => statementIds(rule.statements))
    }
  };
}

function requireTileLookupRule(name: string): TileLookupRule {
  const rule = TILE_LOOKUP_RULES.find((candidate) => candidate.name === name);
  if (!rule) throw new Error(`Missing original collision tile lookup rule: ${name}`);
  return rule;
}

function firstAssignment(label: string, records: readonly AssignmentRef[]): AssignmentRef {
  const record = records[0];
  if (!record?.statementId) throw new Error(`Missing original collision assignment: ${label}`);
  return record;
}

function statementIds(records: readonly StatementRef[]): readonly string[] {
  return records.map((record) => record.statementId).filter((statementId) => statementId.length > 0);
}
